import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  INITIAL_ASSETS_NAMES,
  omitFieldsForTotalAssetsSum,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/InitialAssetsForm/utils/form';
import {
  INITIAL_LIABILITIES_NAMES,
  omitFieldsForTotalLiabilitiesSum,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/InitialLiabilitiesForm/utils/form';
import {
  initialAssetsFormPrefix,
  initialEquityFormPrefix,
  initialLiabilitiesFormPrefix,
  OPENING_BALANCE_SHEETS_TABS_NAMES,
} from 'components/BusinessToolForm/Sections/FinancialPlan/OpeningBalanceSheets/utils/form';
import {
  convertOpenBalanceSheetValues,
  fillPeriodValueWithInitialValue,
} from 'components/BusinessToolForm/utils/helpers';

import { concatWithDot } from 'helpers/string';

import { openingBalanceSheetToProjectedBalanceSheetMap } from './consts';

export const useUpdateOpenBalanceSheet = () => {
  const { setValue, getValues } = useFormContext();

  const currentAssetField = concatWithDot(initialAssetsFormPrefix, INITIAL_ASSETS_NAMES.CURRENT_ASSETS);
  const nonCurrentAssetsField = concatWithDot(initialAssetsFormPrefix, INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS);
  const currentLiabilitiesField = concatWithDot(
    initialLiabilitiesFormPrefix,
    INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES,
  );
  const nonCurrentLiabilitiesField = concatWithDot(
    initialLiabilitiesFormPrefix,
    INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES,
  );

  const updateBalanceSheet = (type: string, values: object) => {
    Object.entries(values).forEach(([key, value]) => {
      const filedName =
        openingBalanceSheetToProjectedBalanceSheetMap[
          type as keyof typeof openingBalanceSheetToProjectedBalanceSheetMap
        ][key];
      if (!filedName) return;
      const periodValue = fillPeriodValueWithInitialValue(value);
      setValue(filedName, periodValue);
    });
  };

  const openBalanceSheetUpdateHandler = useCallback((multiplier: number) => {
    const currentAssets = getValues(concatWithDot(initialAssetsFormPrefix, INITIAL_ASSETS_NAMES.CURRENT_ASSETS));
    const nonCurrentAssets = getValues(concatWithDot(initialAssetsFormPrefix, INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS));
    const currentLiabilities = getValues(
      concatWithDot(initialLiabilitiesFormPrefix, INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES),
    );
    const nonCurrentLiabilities = getValues(
      concatWithDot(initialLiabilitiesFormPrefix, INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES),
    );
    const equity = getValues(initialEquityFormPrefix);
    const updatedCurrentAssets = convertOpenBalanceSheetValues(currentAssets, omitFieldsForTotalAssetsSum, multiplier);
    const updatedNonCurrentAssets = convertOpenBalanceSheetValues(nonCurrentAssets, [], multiplier);
    const updatedCurrentLiabilities = convertOpenBalanceSheetValues(
      currentLiabilities,
      omitFieldsForTotalLiabilitiesSum,
      multiplier,
    );
    const updatedNonCurrentLiabilities = convertOpenBalanceSheetValues(nonCurrentLiabilities, [], multiplier);
    const updatedEquity = convertOpenBalanceSheetValues(equity, [], multiplier);

    setValue(currentAssetField, updatedCurrentAssets);
    setValue(nonCurrentAssetsField, updatedNonCurrentAssets);
    setValue(currentLiabilitiesField, updatedCurrentLiabilities);
    setValue(nonCurrentLiabilitiesField, updatedNonCurrentLiabilities);
    setValue(initialEquityFormPrefix, updatedEquity);

    updateBalanceSheet(INITIAL_ASSETS_NAMES.CURRENT_ASSETS, updatedCurrentAssets);
    updateBalanceSheet(INITIAL_ASSETS_NAMES.NON_CURRENT_ASSETS, updatedNonCurrentAssets);
    updateBalanceSheet(INITIAL_LIABILITIES_NAMES.CURRENT_LIABILITIES, updatedCurrentLiabilities);
    updateBalanceSheet(INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES, updatedNonCurrentLiabilities);
    updateBalanceSheet(INITIAL_LIABILITIES_NAMES.NON_CURRENT_LIABILITIES, updatedNonCurrentLiabilities);
    updateBalanceSheet(OPENING_BALANCE_SHEETS_TABS_NAMES.INITIAL_EQUITY, updatedEquity);
  }, []);

  return openBalanceSheetUpdateHandler;
};
